import axios, {
  AxiosInstance,
  AxiosProgressEvent,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import * as Sentry from "@sentry/react";
import { t } from "@i18n";
import { errorCodes } from "@Helpers/errorCodes";

// ============================================================================
// Shared Types
// ============================================================================

export interface PlatformAdapter {
  storage: {
    getItem: (key: string) => Promise<string | null> | string | null;
    removeItem: (key: string) => Promise<void> | void;
  };
  navigation: {
    getCurrentPath: () => string;
    redirectToLogin: (params?: { redirectUrl?: string; errorMessage?: string }) => void;
  };
  dispatch: (action: unknown) => void;
  actions: {
    setLoggedOut: () => unknown;
    setLicenceInvalid?: () => unknown;
    showToast?: (message: string) => unknown;
  };
  getRequestHeaders?: () => Promise<Record<string, string>> | Record<string, string>;
  // Whether this platform authenticates via browser cookies (default true). Native
  // mobile authenticates with a Bearer header and must set this to false: attaching
  // cookies makes the OS cookie jar replay the persistent jwtToken cookie, which
  // switches the server's CSRF enforcement on for requests that can't satisfy it
  // (the csrfToken session cookie does not survive an app restart).
  useCookieAuth?: boolean;
  excludedPathsFor401?: string[];
  // Resolves the backend origin (e.g. `${window.location.origin}/api-server`). Required so
  // internal requests like the CSRF token fetch hit the backend instead of the frontend host.
  getBackendUrl: () => string;
  // Whether the user has an authenticated session. Used only to suppress the
  // csrf-missing warning on pre-login requests the server never CSRF-checks.
  isAuthenticated?: () => boolean;
}

export interface ApiResponse<T> {
  data: T;
  message?: string;
  success?: boolean;
}

type UnspecifiedResponseType = {
  readonly __brand: "ERROR: You must specify a response type — use axiosWrapper.post<YourResponseType>(...)";
};

type NoAnyGuard<T> = 0 extends 1 & T ? [never] : [];

export interface TypedAxiosInstance {
  post<TResponse = UnspecifiedResponseType, TRequest = unknown>(
    url: string,
    data?: TRequest,
    config?: AxiosRequestConfig,
    ...noAny: NoAnyGuard<TResponse>
  ): Promise<AxiosResponse<TResponse>>;
  get<TResponse = UnspecifiedResponseType>(
    url: string,
    config?: AxiosRequestConfig,
    ...noAny: NoAnyGuard<TResponse>
  ): Promise<AxiosResponse<TResponse>>;
  put<TResponse = UnspecifiedResponseType, TRequest = unknown>(
    url: string,
    data?: TRequest,
    config?: AxiosRequestConfig,
    ...noAny: NoAnyGuard<TResponse>
  ): Promise<AxiosResponse<TResponse>>;
  delete<TResponse = UnspecifiedResponseType>(
    url: string,
    config?: AxiosRequestConfig,
    ...noAny: NoAnyGuard<TResponse>
  ): Promise<AxiosResponse<TResponse>>;
  patch<TResponse = UnspecifiedResponseType, TRequest = unknown>(
    url: string,
    data?: TRequest,
    config?: AxiosRequestConfig,
    ...noAny: NoAnyGuard<TResponse>
  ): Promise<AxiosResponse<TResponse>>;
}

// ============================================================================
// Shared Factory
// ============================================================================

const ERROR_CODES = { LICENCE_EXPIRED: 1001 } as const;
const HTTP_STATUS = { UNAUTHORIZED: 401, FORBIDDEN: 403, TOO_MANY_REQUESTS: 429 } as const;
// Response types whose callers read `res.data` as bytes — `new Uint8Array(res.data)`,
// `URL.createObjectURL(res.data)`, `FileReader.readAsDataURL(res.data)`, pdf.js. Handing one of those
// the `{ status: false }` envelope the network branch synthesizes is worse than the rejection it was
// written against: the envelope is neither a Blob nor an ArrayBuffer, so it fails later and further
// from the cause. Every such call site already sits behind a `catch`, so rejecting is what they expect.
const BINARY_RESPONSE_TYPES = new Set(["arraybuffer", "blob", "stream"]);

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null;

const STATE_CHANGING_METHODS = new Set(["post", "put", "patch", "delete"]);
const CSRF_ERROR_CODES: number[] = [
  errorCodes.CSRF_TOKEN_MISMATCH,
  errorCodes.CSRF_TOKEN_INVALID,
  errorCodes.CSRF_TOKEN_MISSING,
];
let cachedCsrfToken: string | null = null;
let csrfFetchInFlight: Promise<string | null> | null = null;

const CSRF_FETCH_SLOW_THRESHOLD_MS = 2500;

async function fetchCsrfToken(instance: AxiosInstance, csrfUrl: string): Promise<string | null> {
  if (csrfFetchInFlight) return csrfFetchInFlight;
  csrfFetchInFlight = (async () => {
    try {
      const res = await instance.get<{ csrfToken: string | null }>(csrfUrl, {
        withCredentials: true,
      });
      cachedCsrfToken = res.data?.csrfToken ?? null;
      if (!cachedCsrfToken) {
        Sentry.captureMessage("csrf_token_empty_body", { level: "warning", extra: { url: csrfUrl } });
      }
      return cachedCsrfToken;
    } catch (error) {
      Sentry.captureMessage("csrf_token_fetch_failed", {
        extra: { url: csrfUrl, error: error instanceof Error ? error.message : String(error) },
      });
      return null;
    } finally {
      csrfFetchInFlight = null;
    }
  })();
  return csrfFetchInFlight;
}

export function clearCsrfToken() {
  cachedCsrfToken = null;
}

const isInfraFailureStatus = (status: number): boolean => status === 499 || status >= 502;

function serverUnavailableEnvelopeData() {
  return {
    status: false,
    errorCode: errorCodes.SERVER_UNAVAILABLE,
    errorMessage: t(
      "A firewall or proxy issue interrupted the request. Please try again, or contact your system administrator.",
    ),
  };
}

export function createAxiosWrapper(adapter: PlatformAdapter, instance: AxiosInstance = axios) {
  instance.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      const returnedConfig: InternalAxiosRequestConfig = {
        ...config,
        onUploadProgress: (progressEvent: AxiosProgressEvent) => {
          if (config.onUploadProgress && progressEvent.total) {
            const percentage = Math.floor((progressEvent.loaded * 100) / progressEvent.total);
            (config.onUploadProgress as unknown as (p: number, l: number) => void)(percentage, progressEvent.loaded);
          }
        },
      };
      const useCookieAuth = adapter.useCookieAuth ?? true;
      returnedConfig.withCredentials = useCookieAuth;

      if (adapter.getRequestHeaders) {
        const headers = await adapter.getRequestHeaders();
        Object.assign(returnedConfig.headers, headers);
      }

      const method = (returnedConfig.method || "get").toLowerCase();
      const url = returnedConfig.url || "";
      // The CSRF double-submit only exists for cookie sessions; Bearer-auth platforms
      // send no cookies, so the server skips CSRF and the token fetch is dead weight.
      if (useCookieAuth && STATE_CHANGING_METHODS.has(method) && !url.endsWith("/csrf-token")) {
        const csrfUrl = `${adapter.getBackendUrl()}/csrf-token`;
        const csrfAwaitStart = performance.now();
        const token = cachedCsrfToken ?? (await fetchCsrfToken(instance, csrfUrl));
        const csrfAwaitElapsedMs = Math.round(performance.now() - csrfAwaitStart);
        if (csrfAwaitElapsedMs >= CSRF_FETCH_SLOW_THRESHOLD_MS) {
          Sentry.captureMessage("csrf_token_await_slow", {
            level: "warning",
            extra: {
              url,
              method,
              elapsedMs: csrfAwaitElapsedMs,
              thresholdMs: CSRF_FETCH_SLOW_THRESHOLD_MS,
            },
          });
        }
        if (token) {
          returnedConfig.headers.set("X-CSRF-Token", token);
        } else if (adapter.isAuthenticated ? adapter.isAuthenticated() : true) {
          Sentry.captureMessage("csrf_header_missing_on_request", {
            level: "warning",
            extra: {
              url,
              method,
              resolvedTokenPresent: Boolean(token),
              cachedTokenPresent: Boolean(cachedCsrfToken),
              fetchInFlight: Boolean(csrfFetchInFlight),
              pathname: typeof window !== "undefined" ? window.location.pathname : undefined,
            },
          });
          const err = new Error("csrf_header_missing");
          globalThis.dispatchEvent(
            new ErrorEvent("error", { error: err, message: err.message, filename: "axiosShared.ts" }),
          );
          return Promise.reject(err);
        }
      }

      return returnedConfig;
    },
    (error) => Promise.reject(error),
  );

  instance.interceptors.response.use(
    (response) => {
      const data: unknown = response.data;
      if (isRecord(data) && typeof data.errorCode === "number" && CSRF_ERROR_CODES.includes(data.errorCode)) {
        const errorMessage = data.errorMessage;
        if (typeof errorMessage === "string" && errorMessage && adapter.actions.showToast) {
          adapter.dispatch(adapter.actions.showToast(errorMessage));
        }
      }
      return response;
    },
    async (error) => {
      const axiosError = axios.isAxiosError<unknown>(error) ? error : undefined;
      const response = axiosError?.response;
      // A transient network failure (axios ERR_NETWORK) means the request never
      // reached the server or no response came back — offline, dropped connection,
      // aborted socket, DNS/CORS/mixed-content. Axios rejects these with no
      // `error.response`. Left to propagate they become `AxiosError: Network Error`
      // unhandled rejections at every uncaught call site (Sentry onunhandledrejection
      // noise). Normalize them to the same `{ status: false }` envelope the app
      // already uses for failed operations (mirrors the 429 branch below) so callers
      // that check `res.data.status` degrade gracefully instead of throwing. Placed
      // first: a network error has no `error.response`, so it must never reach the
      // 401 logout path. Genuine cancellations (ERR_CANCELED) are intentionally left
      // to reject — callers that abort expect the rejection, and so do the binary
      // callers carved out below: the envelope is only meaningful to a JSON reader.
      if (error?.code === "ERR_NETWORK") {
        const responseType = (error.config as AxiosRequestConfig | undefined)?.responseType;
        if (responseType && BINARY_RESPONSE_TYPES.has(responseType)) return Promise.reject(error);
        return {
          ...(error.response ?? {}),
          // There is no response to spread, so without these the synthesized object has no `status`
          // at all and every caller that branches on `res.status` reads `undefined`. 0 is what a
          // browser reports for a transport that never completed.
          status: error.response?.status ?? 0,
          statusText: error.response?.statusText ?? "Network Error",
          data: {
            status: false,
            errorCode: errorCodes.NETWORK_ERROR,
            // Shown to the user, so it is translated like every other user-facing string. The 429
            // branch below can pass the server's own message through; here the server never answered,
            // so the wrapper has to supply one.
            errorMessage: t("Network error. Please check your connection and try again."),
          },
        };
      }

      const status = String(error?.response?.status);

      if (status === String(HTTP_STATUS.UNAUTHORIZED)) {
        const currentPath = adapter.navigation.getCurrentPath();
        // An external-share recipient (CP-283, /share/:token/...) must NEVER be bounced to sign-in:
        // they have no account to sign in with, and the logout side-effects below would also wipe the
        // session of a LOGGED-IN user who merely opened a share link. Every share-mode network call is
        // supposed to be branched onto public routes — this is the backstop that turns any missed
        // branch into a broken feature instead of a lost recipient. Prefix match, because the exact
        // -match list below can never cover a tokenised path.
        if (currentPath.startsWith("/share/")) {
          return Promise.reject(error);
        }
        if (!adapter.excludedPathsFor401?.includes(currentPath)) {
          await adapter.storage.removeItem("isLogedIn");
          await adapter.storage.removeItem("jwtToken");
          adapter.dispatch(adapter.actions.setLoggedOut());
          adapter.navigation.redirectToLogin({
            redirectUrl: currentPath,
            errorMessage: error?.response?.data?.errorMessage,
          });
        }
      }

      if (
        status === String(HTTP_STATUS.FORBIDDEN) &&
        error?.response?.data?.errorCode === ERROR_CODES.LICENCE_EXPIRED
      ) {
        console.log("licence expired");
        if (adapter.actions.setLicenceInvalid) {
          adapter.dispatch(adapter.actions.setLicenceInvalid());
        }
      }

      if (status === String(HTTP_STATUS.TOO_MANY_REQUESTS)) {
        return {
          ...(error.response ?? {}),
          data: {
            status: false,
            errorCode: errorCodes.ACCOUNT_RATE_LIMIT_EXCEEDED,
            errorMessage: error?.response?.data?.errorMessage,
            retryAfter: error?.response?.data?.retryAfter,
          },
        };
      }

      if (response && isInfraFailureStatus(response.status)) {
        const responseType = axiosError.config?.responseType;
        if (responseType && BINARY_RESPONSE_TYPES.has(responseType)) return Promise.reject(error);
        const envelope = serverUnavailableEnvelopeData();
        if (adapter.actions.showToast) {
          adapter.dispatch(adapter.actions.showToast(envelope.errorMessage));
        }
        return {
          ...response,
          data: envelope,
        };
      }

      return Promise.reject(error);
    },
  );

  const typedAxios: TypedAxiosInstance = {
    post: (url, data, config) => instance.post(url, data, config),
    get: (url, config) => instance.get(url, config),
    put: (url, data, config) => instance.put(url, data, config),
    delete: (url, config) => instance.delete(url, config),
    patch: (url, data, config) => instance.patch(url, data, config),
  };

  return { instance, typedAxios };
}
